import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import React, { useEffect, useState } from "react";
import { convertToMoney } from "../utils/number";
import { formatDate } from "../utils/moment";
import {
  getEstadosCuenta,
  getPlanComplementarioInformacion,
  getReporteEstadoCuenta,
} from "../services/dashboard";
import { downloadPdfFromBase64 } from "../services/utils";
import type { PlanComplementarioCuenta } from "../types/planComplementario";
import {
  listComplementarioEstadosConPdf,
  type ComplementarioEstadoRow,
} from "../utils/planComplementario";

import { Link } from "react-router-dom";
import WithMainLayout from "../hocs/layouts/MainLayout";
import { useAPIs } from "../apis/APIsConfig";
import { useDocTitle } from "../utils/hooks";
import { useGlobalStore } from "../utils/global";
import { useFeatureFlag } from "../utils/featureFlags";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Dashboard = () => {
  useDocTitle("Estado de Cuenta");
  useAPIs();

  const { user } = useGlobalStore();
  const mostrarComplementario = useFeatureFlag("MOSTRAR_COMPLEMENTARIO");
  const [list, setlist] = useState<any[]>([]);
  const [complementarioRows, setComplementarioRows] = useState<
    ComplementarioEstadoRow[]
  >([]);

  useEffect(() => {
    getEstadosCuenta()
      .then((resumen) => {
        if (resumen) {
          setlist(resumen.data.value);
        }
      })
      .catch(() => {});

    const cedula = user?.cedula;
    const token = user?.token;
    if (mostrarComplementario && cedula && token) {
      getPlanComplementarioInformacion(cedula)
        .then((resp) => {
          if (resp?.data?.successed && resp.data.value?.cuenta) {
            setComplementarioRows(
              listComplementarioEstadosConPdf(
                resp.data.value.cuenta as PlanComplementarioCuenta[]
              )
            );
          } else {
            setComplementarioRows([]);
          }
        })
        .catch(() => setComplementarioRows([]));
    } else {
      setComplementarioRows([]);
    }
  }, [user?.cedula, user?.token, mostrarComplementario]);

  function download(fechaCorte) {
    getReporteEstadoCuenta(fechaCorte);
  }

  function downloadComplementario(row: ComplementarioEstadoRow) {
    downloadPdfFromBase64(
      row.pdf,
      `Estado_Cuenta_Complementario_${row.fileSlug}`
    );
  }

  return (
    <>
      <div className="block pt-10 lg:pt-8 pb-[128px]">
        <nav className="pb-8 lg:pb-14">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Mi cuenta</Link>
            </li>
            <li className="breadcrumb-item active">Estado de Cuenta</li>
          </ol>
        </nav>
        <ul className="info-card-download">
          {list.map((item, index) => (
            <li key={index}>
              <div>
                <div className="icon-container">
                  <i className="icon icon-cash text-primary-90 text-[20px]"></i>
                </div>
                <div className="description">
                  <p>Al corte {formatDate(item.fechaCorte, "DD-MM-YYYY")}</p>
                  <h5 className="lead font-semibold">
                    RD$ {convertToMoney(item.monto)}
                  </h5>
                </div>
              </div>
              <button
                onClick={() => download(item.fechaCorte)}
                className="btn btn-secondary btn-medium hidden lg:inline-flex"
              >
                Descargar
              </button>
              <button
                className="block lg:hidden"
                onClick={() => download(item.fechaCorte)}
              >
                <i className="icon icon-download text-gray-50 text-[26px]"></i>
              </button>
            </li>
          ))}
        </ul>

        {mostrarComplementario && complementarioRows.length > 0 ? (
          <>
            <h3 className="text-primary-100 font-bold text-lg pb-4 pt-8">
              Cuenta complementaria
            </h3>
            <ul className="info-card-download">
              {complementarioRows.map((row, index) => (
                <li key={`pc-${index}-${row.fileSlug}`}>
                  <div>
                    <div className="icon-container">
                      <i className="icon icon-cash text-primary-90 text-[20px]"></i>
                    </div>
                    <div className="description">
                      <p>{row.labelAlCorte}</p>
                      <h5 className="lead font-semibold">Plan complementario</h5>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadComplementario(row)}
                    className="btn btn-secondary btn-medium hidden lg:inline-flex"
                  >
                    Descargar
                  </button>
                  <button
                    type="button"
                    className="block lg:hidden"
                    onClick={() => downloadComplementario(row)}
                  >
                    <i className="icon icon-download text-gray-50 text-[26px]"></i>
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </>
  );
};

export default WithMainLayout(Dashboard, "Estado de Cuenta");
